import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ForgotPassword from "../components/ForgotPassword";

const LoginPage = () => {
  const { login, register, verifyTwoFactorLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentParam = searchParams.get("assessment") || "";
  const assessmentId = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(assessmentParam)
    ? assessmentParam
    : null;

  const [isLogin, setIsLogin] = useState(searchParams.get("trial") !== "1");
  const [showForgot, setShowForgot] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [phone, setPhone] = useState("");
  const [twoFactorChallenge, setTwoFactorChallenge] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const handlePhoneChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 11) val = val.substring(0, 11);
    
    if (val.length === 0) {
      setPhone("");
      return;
    }

    if (val.length <= 2) {
      setPhone(`(${val}`);
    } else if (val.length <= 6) {
      setPhone(`(${val.slice(0, 2)}) ${val.slice(2)}`);
    } else if (val.length <= 10) {
      setPhone(`(${val.slice(0, 2)}) ${val.slice(2, 6)}-${val.slice(6)}`);
    } else {
      setPhone(`(${val.slice(0, 2)}) ${val.slice(2, 7)}-${val.slice(7)}`);
    }
  };

  const resetFormForModeChange = () => {
    resetMessages();
    setPassword("");
    if (isLogin) {
      setFullName("");
      setClinicName("");
      setPhone("");
    }
  };

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    setShowForgot(false);
    resetFormForModeChange();
  };

  const handleForgotPassword = () => {
    resetMessages();
    setShowForgot(true);
  };

  const handleBackToLogin = () => {
    setShowForgot(false);
    resetMessages();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const navigateAfterLogin = (authenticatedUser) => {
        if (authenticatedUser?.subscription_status === 'inactive' && authenticatedUser?.role !== 'admin') {
          navigate("/assinatura", { replace: true });
          return;
        }
        const permissions = new Set(authenticatedUser?.permissions || []);
        const destination = authenticatedUser?.role === 'admin'
          ? "/admin"
          : permissions.has('dashboard.view')
            ? "/dashboard"
            : permissions.has('appointments.view')
              ? "/agenda"
              : permissions.has('billing.view')
                ? "/financeiro"
                : "/ajuda";
        navigate(destination, { replace: true });
      };

      if (twoFactorChallenge) {
        const result = await verifyTwoFactorLogin(twoFactorChallenge, twoFactorCode);
        navigateAfterLogin(result.user);
        return;
      }

      if (isLogin) {
        const result = await login(email, password);
        if (result.requiresTwoFactor) {
          setTwoFactorChallenge(result.challengeToken);
          setTwoFactorCode("");
          return;
        }
        navigateAfterLogin(result.user);
      } else {
        const result = await register(
          email,
          password,
          fullName,
          clinicName,
          phone,
          assessmentId
        );

        if (result?.success) {
          setSuccess(
            result.assessmentAttached
              ? "Conta criada e diagnóstico vinculado. Ative a conta pelo e-mail para continuar de onde parou."
              : result.message || "Conta criada com sucesso. Verifique seu e-mail para ativar a conta."
          );

          setIsLogin(true);
          setPassword("");
        } else {
          setError(result?.error || "Erro ao criar conta. Verifique os dados e tente novamente.");
        }
      }
    } catch (err) {
      console.error("Auth submit error:", err);

      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao processar sua solicitação.";

      if (detail === "TEMP_EMAIL_BLOCKED") {
        setError(
          "Identificamos que sua conta foi criada com um e-mail temporário ou descartável. Por segurança e para proteger os dados clínicos da plataforma, não é possível continuar usando o OdontoAssist AI com esse tipo de e-mail. Para continuar seu teste gratuito, cadastre-se novamente usando um e-mail profissional ou pessoal válido."
        );
        return;
      }

      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  if (showForgot) {
    return <ForgotPassword onBack={handleBackToLogin} standalone />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white border rounded-xl shadow-sm p-6">
        <h1 className="text-2xl font-semibold text-center text-gray-900">
          {twoFactorChallenge ? "Verificação em duas etapas" : isLogin ? "Entrar" : "Criar conta"}
        </h1>

        <p className="text-sm text-gray-600 text-center mt-2">
          {twoFactorChallenge
            ? "Digite o código de 6 dígitos do seu aplicativo autenticador ou use um código de recuperação."
            : isLogin
            ? "Acesse sua conta do OdontoAssist AI."
            : "Crie sua conta. Após o cadastro, enviaremos um link de ativação por e-mail. O período de 7 dias grátis começa após a ativação."}
        </p>

        {!isLogin && assessmentId && !twoFactorChallenge && (
          <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
            Seu diagnóstico gratuito foi identificado. Depois da ativação, você poderá continuar de onde parou em Boas Práticas.
          </div>
        )}

        {success && (
          <div className="mt-4 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3">
            {success}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {twoFactorChallenge ? <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Código de verificação</label>
              <input autoFocus inputMode="numeric" autoComplete="one-time-code" value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase().slice(0, 20))} placeholder="000000" className="mt-1 w-full border rounded-md px-3 py-2 text-center tracking-widest focus:ring-purple-500 focus:border-purple-500" required minLength={6} maxLength={20} />
            </div>
            <button type="submit" disabled={loading} className="w-full inline-flex justify-center items-center px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60">{loading ? "Verificando..." : "Verificar e entrar"}</button>
            <button type="button" onClick={() => { setTwoFactorChallenge(""); setTwoFactorCode(""); setPassword(""); resetMessages(); }} className="w-full text-sm text-gray-600 hover:text-gray-800">Voltar ao login</button>
          </> : <>
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nome completo
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nome da clínica
                </label>
                <input
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Telefone
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="(99) 99999-9999"
                  className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              E-mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Senha
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500"
              required
            />
          </div>

          {isLogin && (
            <div className="text-right">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                Esqueci minha senha
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex justify-center items-center px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
          >
            {loading
              ? "Processando..."
              : isLogin
              ? "Entrar"
              : "Criar conta"}
          </button>
          </>}
        </form>

        {!twoFactorChallenge && <div className="mt-6 text-center">
          <button
            type="button"
            onClick={handleToggleMode}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {isLogin
              ? "Ainda não tem conta? Criar conta"
              : "Já tem conta? Entrar"}
          </button>
        </div>}
      </div>
    </div>
  );
};

export default LoginPage;
